import { getApiErrorMessage } from '../../../services/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Plus, Trash2, Search, X, Eye, EyeOff, RefreshCw, Settings, Users, Edit } from 'lucide-react';
import { SortableTableHeader, useSortState, sortData } from '../../../components/SortableTableHeader';
const BACKEND = process.env.REACT_APP_BACKEND_URL;
function apiWithToken(token) { const i = axios.create({ baseURL: BACKEND }); i.interceptors.request.use(c => { c.headers.Authorization = 'Bearer ' + token; return c; }); return i; }

export default function StaffList({ token }) {
  const api = apiWithToken(token);
  const [staff, setStaff] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState({email:'',name:'',role:'admin'});
  const [staffSort, handleStaffSort] = useSortState('name', 'asc');

  useEffect(() => {
    api.get('/api/eyvo-admin/staff').then(r => setStaff(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  const addStaff = async () => {
    if (!newForm.email || !newForm.name) { toast.error('Email and name required'); return; }
    try {
      await api.post('/api/eyvo-admin/staff', newForm);
      toast.success('Staff member added. They must sign in with Google using this email.');
      setNewOpen(false);
      api.get('/api/eyvo-admin/staff').then(r => setStaff(r.data));
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Staff members sign in with their <strong>Google account</strong>. Add their Google email below.
        </p>
        <Button size="sm" onClick={() => setNewOpen(true)}><Plus className="h-4 w-4 mr-1" />Add Staff</Button>
      </div>
      <div className="border rounded-xl bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b bg-muted/20">
            <SortableTableHeader label="Name" field="name" sortState={staffSort} onSort={handleStaffSort} />
            <SortableTableHeader label="Email (Google)" field="email" sortState={staffSort} onSort={handleStaffSort} />
            <SortableTableHeader label="Role" field="role" sortState={staffSort} onSort={handleStaffSort} />
            <SortableTableHeader label="Last Login" field="last_login" sortState={staffSort} onSort={handleStaffSort} />
          </tr></thead>
          <tbody>{sortData(staff, staffSort.field, staffSort.dir).map(s => (
            <tr key={s.email} className="border-b last:border-0">
              <td className="px-4 py-2.5 font-medium">
                {s.google_picture && <img src={s.google_picture} alt="" className="w-6 h-6 rounded-full inline-block mr-2" />}
                {s.google_name || s.name}
              </td>
              <td className="px-4 py-2.5">{s.email}</td>
              <td className="px-4 py-2.5 capitalize">{s.role}</td>
              <td className="px-4 py-2.5 text-muted-foreground">{s.last_login ? new Date(s.last_login).toLocaleString() : 'Never'}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>

      {newOpen && (
        <Dialog open onOpenChange={() => setNewOpen(false)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Eyvo Staff Member</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-800">
                The staff member must sign in using the <strong>Google account associated with this email</strong>.
                No password is set — Google handles all authentication.
              </div>
              <div className="space-y-1"><Label>Full Name</Label><Input value={newForm.name} onChange={e=>setNewForm(p=>({...p,name:e.target.value}))} /></div>
              <div className="space-y-1"><Label>Google Email</Label><Input type="email" value={newForm.email} onChange={e=>setNewForm(p=>({...p,email:e.target.value.toLowerCase()}))} placeholder="staff@eyvo.com" /></div>
              <div className="space-y-1"><Label>Role</Label>
                <select value={newForm.role} onChange={e=>setNewForm(p=>({...p,role:e.target.value}))} className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm">
                  <option value="superadmin">Superadmin</option>
                  <option value="admin">Admin</option>
                  <option value="support">Support</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={()=>setNewOpen(false)}>Cancel</Button>
              <Button onClick={addStaff}>Add Staff Member</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
